import { useLazyQuery } from '@apollo/client';
import React, { FunctionComponent, useEffect } from 'react';
import { Article as TArticle, User as TUser } from 't2-graphql-types';
import Article from './Article';
import getArticleDataById from './queries/getArticleDataById';
import { useAuthenticatedUser } from '../Auth/hooks/useAuthenticatedUser';
import { getUsersWhoReadArticleByArticleId } from './queries/getUsersWhoReadArticleByArticleId';
import { useT2Query } from '../../hooks/useT2Query';
import { useLazyArticleNftReferencesQuery } from '../ArticleCollect/hooks/useArticleNftReferencesQuery';

export type TArticleContainerProps = {
  articleId: string;
  shouldEnableEdit?: boolean;
  forceLoading?: boolean;
};

type TArticleInteractor = Pick<TUser, 'id' | 'publicAddress' | 'profileImage' | 'name'>;

type TArticleInteractionRelationship = {
  user: TArticleInteractor;
  timestamp: string;
};

type TArticleInteractionQueryResponse = {
  userArticleRelations: TArticleInteractionRelationship[];
};

export const ArticleContainer: FunctionComponent<TArticleContainerProps> = ({
  articleId,
  shouldEnableEdit = true,
  forceLoading = false,
}) => {
  const { data, loading, error } = useT2Query(getArticleDataById, {
    returnPartialData: true,
    variables: { articleId },
  });
  const [getArticleNftReferences, nftReferences] = useLazyArticleNftReferencesQuery({ articleId });
  const [handleGetUsersWhoReadArticle, { data: userArticleRelationsData }] =
    useLazyQuery<TArticleInteractionQueryResponse>(getUsersWhoReadArticleByArticleId);
  const { authenticatedUserId } = useAuthenticatedUser();
  const article: Required<TArticle> = data?.article;
  const authorId = article?.author?.id;
  const isArticleAuthor = authenticatedUserId === authorId;
  const usersWhoReadArticle =
    userArticleRelationsData?.userArticleRelations?.map(({ user, timestamp }) => ({
      ...user,
      timestamp,
    })) || [];

  useEffect(() => {
    if ((authorId && !loading) || !error) {
      handleGetUsersWhoReadArticle({ variables: { articleId, authorId } });
    }
  }, [authorId, articleId, loading, error]);

  useEffect(() => {
    if (articleId) {
      getArticleNftReferences({ variables: { articleId } });
    }
  }, [articleId]);

  const nftReference = nftReferences.data?.nftReferences?.[0];

  const collect = {
    hasOnchainNftReference: !!nftReference?.address,
    loading: !!nftReferences?.loading,
    nftReference: nftReference || null,
  };

  return (
    <Article
      loading={loading || forceLoading}
      shouldEnableEdit={shouldEnableEdit && isArticleAuthor}
      usersWhoReadArticle={usersWhoReadArticle || []}
      collect={collect}
      {...article}
    />
  );
};

export default ArticleContainer;
